import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Mail, MessageSquare, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [contactsRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/newsletter`)
      ]);
      setContactSubmissions(contactsRes.data);
      setNewsletterSubscribers(subscribersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-12">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-stone-900 mb-2">Admin Dashboard</h1>
          <p className="text-stone-600">Manage contact submissions and newsletter subscribers</p>
        </div>

        <Tabs defaultValue="contacts" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="contacts" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Contact Submissions
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Newsletter
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contacts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Contact Form Submissions</span>
                  <span className="text-sm font-normal text-stone-600">{contactSubmissions.length} total</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-stone-600">Loading...</p>
                ) : contactSubmissions.length === 0 ? (
                  <p className="text-stone-600">No submissions yet</p>
                ) : (
                  <div className="space-y-4">
                    {contactSubmissions.map((submission) => (
                      <div key={submission.id} className="border border-stone-200 rounded-lg p-6 bg-white">
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-stone-500">Name</p>
                            <p className="font-semibold text-stone-900">{submission.name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-stone-500">Email</p>
                            <p className="font-semibold text-stone-900">{submission.email}</p>
                          </div>
                          <div>
                            <p className="text-sm text-stone-500">Phone</p>
                            <p className="font-semibold text-stone-900">{submission.phone || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-stone-500">Date</p>
                            <p className="font-semibold text-stone-900">{new Date(submission.date).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-stone-500 mb-2">Message</p>
                          <p className="text-stone-700">{submission.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="newsletter">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Newsletter Subscribers</span>
                  <span className="text-sm font-normal text-stone-600">{newsletterSubscribers.length} subscribers</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-stone-600">Loading...</p>
                ) : newsletterSubscribers.length === 0 ? (
                  <p className="text-stone-600">No subscribers yet</p>
                ) : (
                  <div className="space-y-2">
                    {newsletterSubscribers.map((email, idx) => (
                      <div key={idx} className="border border-stone-200 rounded-lg p-4 bg-white flex items-center gap-3">
                        <Users className="w-5 h-5 text-amber-700" />
                        <p className="font-medium text-stone-900">{email}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-8">
          <Button onClick={loadData} className="bg-amber-600 hover:bg-amber-700 text-white">
            Refresh Data
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Admin;